import { Button, Form, Spinner } from 'react-bootstrap';
import { memo, useRef, useState } from 'react';
import Papa from 'papaparse';
import { useAppSelector } from '../../../store/store';
import { displayErrorToast, displaySuccessToast } from '../../../utils/ToastUtils';
import { addCorrectFontsInTemplate } from '../../../utils/template.util';
import { getDesignElements } from '../../../store/selector/brand-elements.selector';
import { generateRandomId } from '../../../utils/common.utils';

const Variants = ({ store }: { store: any }) => {
    const designElementsData = useAppSelector(getDesignElements);

    const [variantsLoading, setVariantsLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const downloadSampleCSV = () => {
        const storeJSON = store.toJSON();
        const projectJSON = addCorrectFontsInTemplate(storeJSON, designElementsData);
        const pages = projectJSON.pages;

        if (pages.length === 0) {
            displayErrorToast('No page to download sample csv');
            return;
        }

        const firstPage = pages[0];
        const headers = firstPage.children
            .filter((child: any) => child.type === 'text' && child.name)
            .map((child: any) => child.name);

        if (headers.length === 0) {
            displayErrorToast('No text layer to download sample csv');
            return;
        }

        const csv = Papa.unparse([
            headers.reduce(
                (acc: any, header: any) => {
                    acc[header] = '';
                    return acc;
                },
                {} as Record<string, string>,
            ),
        ]);

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'variants_sample.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleBulkUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.type !== 'text/csv') {
            alert('Please upload a valid CSV file.');
            return;
        }

        setVariantsLoading(true);

        setTimeout(() => {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: function (results) {
                    const rows = results.data as Record<string, string>[];
                    const storeJSON = store.toJSON();
                    const projectJSON = addCorrectFontsInTemplate(storeJSON, designElementsData);
                    const pages = projectJSON.pages;
                    if (pages.length > 0) {
                        const firstPage = { ...pages[0] };
                        const newPages = [firstPage];

                        rows.forEach((row, index) => {
                            const clonedPage = JSON.parse(JSON.stringify(firstPage));
                            clonedPage.id = generateRandomId();
                            clonedPage.children = clonedPage.children.map((child: any) => ({
                                ...child,
                                id: generateRandomId(),
                            }));

                            Object.entries(row).forEach(([columnName, value]) => {
                                clonedPage.children.forEach((child: any) => {
                                    if (
                                        child.type === 'text' &&
                                        child.name &&
                                        child.name.trim().toLowerCase() ===
                                            columnName.trim().toLowerCase() &&
                                        value.length > 0
                                    ) {
                                        child.text = value;
                                    }
                                });
                            });

                            newPages.push(clonedPage);
                        });

                        projectJSON.pages = newPages;
                        store.loadJSON(projectJSON);
                        displaySuccessToast(`Created ${rows.length} variants`);
                    }

                    setVariantsLoading(false);
                },
                error: function () {
                    event.target.value = '';
                    displayErrorToast('Please try to upload file again');
                    setVariantsLoading(false);
                },
            });

            event.target.value = '';
        }, 2000);
    };

    return (
        <>
            <div className="polotnoEditor">
                <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <Button
                    variant="outline-primary"
                    className="tw-w-full tw-mt-5"
                    onClick={downloadSampleCSV}
                >
                    Download Sample CSV
                </Button>
                <Button
                    disabled={variantsLoading}
                    variant="outline-primary"
                    className="tw-w-full tw-mt-5"
                    onClick={handleBulkUploadClick}
                >
                    Bulk Upload CSV
                    {variantsLoading && <Spinner className="tw-w-6 tw-h-6" />}
                </Button>
            </div>
        </>
    );
};

export default memo(Variants);
